export type Language = 'tr' | 'en'

export interface Strings {
  // Navigation
  nav: {
    home: string
    about: string
    contact: string
    login: string
  }
  
  // Footer
    footer: {
      company: string
      support: string
      about: string
      contact: string
      support_page: string
      privacy: string
      description: string
      address: string
      fullAddress: string
      email: string
      phone: string
  }
  
  // Common UI elements
  common: {
    loading: string
    error: string
    success: string
    cancel: string
    save: string
    delete: string
    edit: string
    search: string
    filter: string
    back: string
    next: string
    previous: string
    close: string
    confirm: string
    yes: string
    no: string
    add: string
    remove: string
    select: string
  }

  // Comment related (root level for backward compatibility)
  confirmDeleteComment: string
  noCommentsYet: string
  editComment: string
  deleteComment: string
  saving: string
  save: string
  cancel: string
  showLess: string
  readMore: string
  writeComment: string
  turbineNotFound: string
  backToDashboard: string
  selectPhoto: string
  noPhotosFound: string

  // Loading messages
  loading: {
    loadingTurbineData: string
    dataPreparation: string
    panoramicViewLoading: string
    turbineDataAndPhotosLoading: string
    deepwindPreparing: string
    authAndDataLoading: string
    photosLoading: string
    reportsLoading: string
    companiesLoading: string
    usersLoading: string
    turbinesLoading: string
    goBack: string
  }
  
  // Home page
  home: {
    title: string
    subtitle: string
    description: string
    heroTitle: string
    heroSubtitle: string
    heroDescription: string
    getStarted: string
    learnMore: string
    features: {
      title: string
      subtitle: string
    }
    partnerships: {
      title: string
      subtitle: string
    }
    technologies: {
      title: string
      subtitle: string
      ai: string
      machineLearning: string
      iotSensors: string
      cloudComputing: string
      view360: string
        predictiveAnalytics: string
        realTimeMonitoring: string
        bigData: string
        computerVision: string
        edgeDetection: string
        damageDetection: string
        anomalyDetection: string
    }
  }
  
  // About page
  about: {
    title: string
    description: string
    mission: string
    vision: string
    values: {
      sustainability: string
      innovation: string
      efficiency: string
      quality: string
      sustainabilityDesc: string
      innovationDesc: string
      efficiencyDesc: string
      qualityDesc: string
    }
    heroDescription: string
    storyTitle: string
    storyParagraph1: string
    storyParagraph2: string
    storyParagraph3: string
    turbineCountTitle: string
    turbineCountDescription: string
    missionTitle: string
    missionDescription: string
    visionTitle: string
    visionDescription: string
    valuesTitle: string
    valuesSubtitle: string
      ctaTitle: string
      ctaDescription: string
      ctaButton: string
      storyLabel: string
      turbineLabel: string
  }
  
  // Contact page
  contact: {
    title: string
    description: string
    name: string
    nameRequired: string
    namePlaceholder: string
    email: string
    phone: string
    phonePlaceholder: string
    subject: string
    subjectRequired: string
    subjectPlaceholder: string
    message: string
    messageRequired: string
    messagePlaceholder: string
    send: string
    sending: string
    success: string
    error: string
    address: string
    workingHours: string
    monday: string
    tuesday: string
    wednesday: string
    thursday: string
    friday: string
    saturday: string
    sunday: string
    closed: string
    sendMessage: string
  }
  
  // Auth pages
  auth: {
    login: {
      title: string
      email: string
      password: string
      emailLabel: string
      emailPlaceholder: string
      passwordLabel: string
      passwordPlaceholder: string
      loginButton: string
      loggingIn: string
      forgotPassword: string
      resetPassword: string
      noAccount: string
      register: string
      loginError: string
      welcome: string
      subtitle: string
      securityNotice: string
      securityText: string
      sslProtected: string
      encryption: string
      monitoring: string
    }
    passwordReset: {
      title: string
      description: string
      email: string
      emailLabel: string
      emailPlaceholder: string
      sendLink: string
      sendButton: string
      sending: string
      backToLogin: string
      success: string
      successTitle: string
      successMessage: string
      warningMessage: string
      backToLoginButton: string
      resendButton: string
      error: string
      emailRequired: string
      emailInvalid: string
    }
  }
  
  // Dashboard
  dashboard: {
    title: string
    turbines: string
    users: string
    companies: string
    reports: string
    settings: string
    logout: string
    welcome: string
    totalTurbines: string
    activeTurbines: string
    totalReports: string
    systemHealth: string
    companyName: string
    turbineManagement: string
    addTurbine: string
    partnerManagement: string
    userSettings: string
    searchPlaceholder: string
    resultsFound: string
    noResults: string
    noResultsMessage: string
    allCities: string
    allYears: string
    allCompanies: string
    selectCompany: string
    pleaseSelectCompany: string
    errorLoadingCompanies: string
    errorLoadingTurbines: string
    errorAddingTurbine: string
    errorDeletingTurbine: string
    errorUpdatingTurbine: string
    apiError: string
    active: string
    maintenance: string
    faulty: string
    offline: string
    inactive: string
    unknown: string
    error: string
    turbineRooms: string
    turbinePhotos2D: string
    turbinePhotos360: string
    photo: string
    addPhoto: string
    view360: string
    edit: string
    delete: string
    addNewTurbine: string
    editTurbine: string
    deleteTurbine: string
    confirmDeleteMessage: string
    confirmAction: string
    deleteWarning1: string
    deleteWarning2: string
    deleteWarning3: string
    confirmDelete: string
    cancel: string
    save: string
    add: string
    turbineName: string
    turbineNamePlaceholder: string
    city: string
    cityName: string
    cityPlaceholder: string
    company: string
    turbineCode: string
    codePlaceholder: string
    location: string
    locationPlaceholder: string
  }
  
  // Turbine management
  turbine: {
    title: string
    name: string
    location: string
    status: string
    createdAt: string
    actions: string
    view: string
    edit: string
    delete: string
    add: string
    search: string
    filter: string
    noData: string
    loading: string
    addNew: string
    editTurbine: string
    deleteTurbine: string
    confirmDelete: string
    deleteSuccess: string
    saveSuccess: string
    rooms: string
    photos: string
    reports: string
    details: string
    roomCount: string
    updateRooms: string
  }
  
  // Turbine Detail Screen
  turbineDetail: {
    photos: {
      title: string
      bladePhotos: string
      dronePhotos: string
      operatorPhotos: string
      robotPhotos: string
      viewPhotos: string
      addPhoto: string
      uploadPhotos: string
      selectPhotos: string
      selectedPhotos: string
      selectPhoto: string
      selectPhotoDescription: string
      noPhotosFound: string
      backToTurbineDetail: string
      zoomInstruction: string
      uploadButton: string
      uploading: string
      year: string
      allYears: string
      search: string
      compare: string
      cancelCompare: string
      comparePhotos: string
      month: string
      photoCount: string
      uploader: string
      noPhotos: string
      noPhotosDescription: string
      openComments: string
      comments: string
      logout: string
      bladeSelectionAndPhotos: string
      bladeSelectionDescription: string
      // Zoom and annotation
      zoomIn: string
      zoomOut: string
      resetView: string
      annotationMode: string
      exitAnnotationMode: string
      exitMode: string
      addMarker: string
      hideMarkers: string
      showMarkers: string
      deleteMarker: string
      addMarkerLabel: string
      markerColor: string
      markerPlaceholder: string
      save: string
      clickToAddMarker: string
      markers: string
      // Version labels
      version: string
      edit: string
      original: string
    }
    rooms: {
      selectRoom: string
      room: string
      editRooms: string
      roomCount: string
      updateRoomCount: string
    }
    reports: {
      title: string
      generate: string
      year: string
      existing: string
      noReports: string
      download: string
      delete: string
      generating: string
    }
    comments: {
      title: string
      add: string
      placeholder: string
      submit: string
      noComments: string
      firstComment: string
    }
  }
  
  // User Management
  users: {
    title: string
    name: string
    email: string
    role: string
    company: string
    status: string
    active: string
    inactive: string
          admin: string
      operator: string
      superadmin: string
      customer: string
      user: string
      addUser: string
    editUser: string
    deleteUser: string
    search: string
    filter: string
  }
  
  // Company Management
  companies: {
    title: string
    name: string
    email: string
    phone: string
    address: string
    userCount: string
    turbineCount: string
    addCompany: string
    editCompany: string
    deleteCompany: string
    search: string
  }
  
  // Support & FAQ
  support: {
    title: string
    description: string
    contactUs: string
    documentation: string
    tutorials: string
  }
  // Privacy
    privacy: {
      title: string
      description: string
      lastUpdated: string
    },

    // Blog
    blog: {
      sectionTitle: string
      sectionDescription: string
      previousPost: string
      nextPost: string
      readMore: string
      categoryTechnology: string
      categoryInspection: string
      categoryEducation: string
      categoryFieldWork: string
      videoLabel: string
      notFound: string
      backToHome: string
      galleryTitle: string
      galleryImageAlt: string

      // Blog posts content
      posts: {
        fieldWorkNacelleRobot: {
          title: string
          subtitle: string
          date: string
          readTime: string
          heroTitle: string
          introParagraph1: string
          introParagraph2: string
          imageCaption1: string
          imageCaption2: string
          imageCaption3: string
          deploymentTitle: string
          deploymentParagraph1: string
          deploymentParagraph2: string
          resultTitle: string
          resultParagraph: string
          conclusion: string
        }
        robotLidarInspection: {
          title: string
          subtitle: string
          date: string
          readTime: string
          heroTitle: string
          introParagraph1: string
          introParagraph2: string
          anomalyTitle: string
          anomalyParagraph1: string
          anomalyParagraph2: string
          reportTitle: string
          reportParagraph1: string
          conclusion: string
        }
        droneRobotInspection: {
          title: string
          subtitle: string
          date: string
          readTime: string
          heroTitle: string
          introParagraph: string
          imageCaption1: string
          imageCaption2: string
          imageCaption3: string
          gridCaption: string
          processParagraph: string
          conclusion: string
        }
        windTurbineTechnology: {
          title: string
          subtitle: string
          date: string
          readTime: string
          heroTitle: string
          introParagraph: string
          workingTitle: string
          workingParagraph1: string
          workingParagraph2: string
          turkeyTitle: string
          turkeyParagraph: string
          regionsTitle: string
          region1Name: string
          region1Desc: string
          region2Name: string
          region2Desc: string
          region3Name: string
          region3Desc: string
          region4Name: string
          region4Desc: string
          region5Name: string
          region5Desc: string
          region6Name: string
          region6Desc: string
          region7Name: string
          region7Desc: string
          region8Name: string
          region8Desc: string
          advantagesTitle: string
          advantagesParagraph: string
          conclusion: string
        }
        worldsBiggestTurbine: {
          title: string
          subtitle: string
          date: string
          readTime: string
          heroTitle: string
          introParagraph: string
          specTitle: string
          specParagraph: string
          powerTitle: string
          powerParagraph: string
          futureTitle: string
          futureParagraph: string
          conclusion: string
        }
        ropeAccessTurbine: {
          title: string
          subtitle: string
          date: string
          readTime: string
          heroTitle: string
          introParagraph: string
          safetyTitle: string
          safetyParagraph: string
          advantagesTitle: string
          advantage1: string
          advantage2: string
          advantage3: string
          advantage4: string
          conclusion: string
        }
        offshoreTurbines: {
          title: string
          subtitle: string
          date: string
          readTime: string
          heroTitle: string
          introParagraph: string
          advantagesTitle: string
          advantagesParagraph: string
          challengesTitle: string
          challengesParagraph: string
          turkeyTitle: string
          turkeyParagraph: string
          conclusion: string
        }
      }
    },

    robot: {
      hero: {
        advancedTech: string
        liveInspection: string
        expertTeam: string
        precisionScanning: string
        controlCenter: string
        damageAnalysis: string
        techTitle: string
        inspectionTitle: string
        teamTitle: string
        processTitle: string
        centerTitle: string
        bladeTitle: string
        techDesc: string
        inspectionDesc: string
        teamDesc: string
        processDesc: string
        centerDesc: string
        bladeDesc: string
      }
      damage: {
        badge: string
        title: string
        description: string
        detected: string
        detection1: string
        detection2: string
        detection3: string
        detection4: string
      }
      gallery: {
        badge: string
        title: string
        description: string
        robotOp: string
        equipment: string
        interior: string
        upper: string
        platform: string
        general: string
        control: string
        bladeInspection: string
        detailedInspection: string
        bladeScanning: string
        blade: string
        surface: string
      }
    },

    // Common UI labels
    ui: {
      videoLabel: string,
      robotInspection: string,
      goToMedia: string,
      turbine: string
    }

  // Errors
  errors: {
    notFound: string
    serverError: string
    unauthorized: string
    networkError: string
    tryAgain: string
    goHome: string
    // Hata gosterim altyapisi (ApiError.kind -> mesaj)
    title: string
    generic: string
    network: string
    timeout: string
    sessionExpired: string
    permission: string
    validation: string
    conflict: string
    rateLimit: string
    server: string
    notFoundResource: string
    loadFailed: string
    retry: string
    dismiss: string
    fileTooLarge: string
    invalidFileType: string
    partialUploadTitle: string
    partialUploadSuffix: string
    renderCrashTitle: string
    renderCrashBody: string
    reloadPage: string
  }
}

export const strings: Record<Language, Strings> = {
  tr: {
    nav: {
      home: 'Ana Sayfa',
      about: 'Hakkımızda',
      contact: 'İletişim',
      login: 'Giriş Yap'
    },
    
    footer: {
      company: 'Şirket',
      support: 'Destek',
      about: 'Hakkımızda',
      contact: 'İletişim',
      support_page: 'Destek',
      privacy: 'Gizlilik',
      description: 'Modern teknolojilerle iş süreçlerinizi dijitalleştiriyor, ölçeklenebilir ve güvenli çözümler sunuyoruz.',
      address: 'İZMİR/TÜRKİYE',
      fullAddress: 'VentraWind Teknoloji A.Ş., Adalet Mahallesi, Manas Bulvarı No: 47/A, Folkart Towers A Kule Kat: 12, 35535 Bayraklı/İZMİR',
      email: 'info@ventrawind.com',
      phone: '0 534 495 25 90'
    },
    
    common: {
      loading: 'Yükleniyor...',
      error: 'Hata',
      success: 'Başarılı',
      cancel: 'İptal',
      save: 'Kaydet',
      delete: 'Sil',
      edit: 'Düzenle',
      search: 'Ara',
      filter: 'Filtrele',
      back: 'Geri',
      next: 'İleri',
      previous: 'Önceki',
      close: 'Kapat',
      confirm: 'Onayla',
      yes: 'Evet',
      no: 'Hayır',
      add: 'Ekle',
      remove: 'Kaldır',
      select: 'Seç'
    },

    // Comment related (root level)
    confirmDeleteComment: 'Bu yorumu silmek istediğinizden emin misiniz?',
    noCommentsYet: 'Henüz yorum yok',
    editComment: 'Yorumu düzenle',
    deleteComment: 'Yorumu sil',
    saving: 'Kaydediliyor...',
    save: 'Kaydet',
    cancel: 'İptal',
    showLess: 'Daha az göster',
    readMore: 'Devamını oku',
    writeComment: 'Yorum yaz...',
    turbineNotFound: 'Türbin bulunamadı',
    backToDashboard: 'Ana Sayfaya Dön',
    selectPhoto: 'Fotoğraf seçin',
    noPhotosFound: 'Fotoğraf bulunamadı',

    loading: {
      loadingTurbineData: 'Türbin Bilgileri Yükleniyor',
      dataPreparation: 'Veriler hazırlanıyor, lütfen bekleyin...',
      panoramicViewLoading: 'Panoramik Görünüm Hazırlanıyor',
      turbineDataAndPhotosLoading: 'Türbin verileri ve fotoğraflar yükleniyor...',
      deepwindPreparing: 'VentraWind Hazırlanıyor',
      authAndDataLoading: 'Kimlik doğrulanıyor ve veriler yükleniyor...',
      photosLoading: 'Fotoğraflar yükleniyor...',
      reportsLoading: 'Raporlar yükleniyor...',
      companiesLoading: 'Şirketler yükleniyor...',
      usersLoading: 'Kullanıcılar yükleniyor...',
      turbinesLoading: 'Türbinler yükleniyor...',
      goBack: 'Geri Dön'
    },
    
    home: {
      title: 'VentraWind Technology',
      subtitle: 'Profesyonel Teknoloji Çözümleri',
      description: 'Modern teknolojilerle iş süreçlerinizi dijitalleştiriyor, ölçeklenebilir ve güvenli çözümler sunuyoruz.',
      heroTitle: 'Rüzgar Enerjisinde',
      heroSubtitle: 'Yeni Nesil İzleme',
      heroDescription: '360° görüntüleme, AI destekli analiz ve gerçek zamanlı izleme ile türbin performansınızı maksimuma çıkarın.',
      getStarted: 'Başlayın',
      learnMore: 'Daha Fazla Bilgi',
      features: {
        title: 'Özellikler',
        subtitle: 'Size en iyi hizmeti sunmak için buradayız'
      },
      partnerships: {
        title: 'Çalışma Ortaklarımız',
        subtitle: 'Güvenilir iş ortaklarımızla birlikte başarıya ulaşıyoruz'
      },
      technologies: {
        title: 'Teknolojilerimiz',
        subtitle: 'En gelişmiş teknolojilerle akıllı çözümler geliştiriyoruz',
        ai: 'Yapay Zeka',
        machineLearning: 'Makine Öğrenmesi',
        iotSensors: 'IoT Sensörler',
        cloudComputing: 'Cloud Computing',
        view360: '360° Görüntüleme',
        predictiveAnalytics: 'Predictive Analytics',
        realTimeMonitoring: 'Real-time Monitoring',
        bigData: 'Big Data',
        computerVision: 'Computer Vision',
        edgeDetection: 'Edge Detection',
        damageDetection: 'Damage Detection',
        anomalyDetection: 'Anomaly Detection'
      }
    },
    
    about: {
      title: 'Hakkımızda',
      description: 'VentraWind olarak, teknoloji alanında yenilikçi çözümler sunuyoruz.',
      mission: 'Misyonumuz',
      vision: 'Vizyonumuz', 
      values: {
      sustainability: 'Sürdürülebilirlik',
      innovation: 'İnovasyon',
      efficiency: 'Verimlilik',
      quality: 'Kalite',
      sustainabilityDesc: 'Temiz enerji geleceği için çevre dostu teknolojiler geliştiriyoruz.',
      innovationDesc: 'Sürekli araştırma ve geliştirme ile sektörde öncü olmaya devam ediyoruz.',
      efficiencyDesc: 'Müşterilerimizin enerji üretim verimliliğini maksimuma çıkarıyoruz.',
      qualityDesc: 'Uluslararası standartlarda kaliteli hizmet sunuyoruz.'
    },
      heroDescription: '2018 yılından beri rüzgar enerjisi sektöründe yenilikçi teknolojilerle sürdürülebilir enerji geleceğine katkıda bulunuyoruz.',
      storyTitle: 'Hikayemiz',
      storyParagraph1: 'VentraWind, İzmir\'de kurulmuş bir teknoloji şirketi olarak, rüzgar enerjisi tesislerinin izlenmesi ve yönetimi konusunda uzmanlaşmıştır.',
      storyParagraph2: 'Türkiye\'nin rüzgar enerjisi potansiyelini en verimli şekilde değerlendirmek amacıyla 360° görüntüleme teknolojisi, yapay zeka destekli analiz sistemleri ve gerçek zamanlı izleme çözümleri geliştiriyoruz.',
      storyParagraph3: 'Bugün 150\'den fazla rüzgar türbininin izlenmesinden sorumlu olan ekibimiz, sürekli araştırma ve geliştirme faaliyetleriyle sektörde yenilikçi çözümler sunmaya devam ediyor.',
      turbineCountTitle: '150+ Türbin İzleniyor',
      turbineCountDescription: 'Türkiye genelinde farklı coğrafi bölgelerde bulunan rüzgar türbinlerini 7/24 izliyor ve optimize ediyoruz.',
      missionTitle: 'Misyonumuz',
      missionDescription: 'Rüzgar enerjisi tesislerinde en ileri teknolojileri kullanarak, enerji üretim verimliliğini artırmak, bakım maliyetlerini düşürmek ve sürdürülebilir enerji geleceğine katkıda bulunmak.',
      visionTitle: 'Vizyonumuz',
      visionDescription: 'Türkiye\'nin ve bölgenin rüzgar enerjisi izleme teknolojilerinde öncü şirketi olmak, yapay zeka destekli çözümlerle global pazarda tanınan bir marka haline gelmek.',
      valuesTitle: 'Değerlerimiz',
      valuesSubtitle: 'İş yapma şeklimizi belirleyen temel değerlerimiz',
      ctaTitle: 'Bizimle İletişime Geçin',
      ctaDescription: 'Rüzgar enerjisi projeleriniz için nasıl destek olabileceğimizi öğrenin.',
      ctaButton: 'İletişime Geç',
      storyLabel: 'Hikayemiz',
      turbineLabel: 'Türbin'
    },
    
    contact: {
      title: 'İletişim',
      description: 'Projeleriniz hakkında konuşmak veya sorularınızı yanıtlamak için buradayız. Bize ulaşın ve birlikte çalışalım.',
      name: 'Ad Soyad',
      nameRequired: 'Ad Soyad *',
      namePlaceholder: 'Adınızı ve soyadınızı girin',
      email: 'E-posta',
      phone: 'Telefon',
      phonePlaceholder: 'Telefon numaranızı girin',
      subject: 'Konu',
      subjectRequired: 'Konu *',
      subjectPlaceholder: 'Mesajınızın konusunu girin',
      message: 'Mesaj',
      messageRequired: 'Mesaj *',
      messagePlaceholder: 'Mesajınızı buraya yazın...',
      send: 'Mesaj Gönder',
      sending: 'Gönderiliyor...',
      success: 'Mesajınız başarıyla gönderildi!',
      error: 'Mesaj gönderilirken bir hata oluştu',
      address: 'Adres',
      workingHours: 'Çalışma Saatleri',
      monday: 'Pazartesi',
      tuesday: 'Salı', 
      wednesday: 'Çarşamba',
      thursday: 'Perşembe',
      friday: 'Cuma',
      saturday: 'Cumartesi',
      sunday: 'Pazar',
      closed: 'Kapalı',
      sendMessage: 'Mesaj Gönderin'
    },
    
    auth: {
      login: {
        title: 'Giriş Yap',
        email: 'E-posta',
        password: 'Şifre',
        emailLabel: 'E-posta Adresi',
        emailPlaceholder: 'ornek@email.com',
        passwordLabel: 'Şifre',
        passwordPlaceholder: '••••••••',
        loginButton: 'Giriş Yap',
        loggingIn: 'Giriş yapılıyor...',
        forgotPassword: 'Şifremi Unuttum',
        resetPassword: 'Şifremi Sıfırla',
        noAccount: 'Hesabınız yok mu?',
        register: 'Kayıt Ol',
        loginError: 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.',
        welcome: 'Hoş Geldiniz',
        subtitle: 'Hesabınıza giriş yapın',
        securityNotice: 'Güvenlik Bildirimi',
        securityText: 'Verileriniz 256-bit SSL şifrelemesi ile korunmaktadır. Tüm giriş denemeleri izlenmekte ve güvenlik protokollerimize uygun şekilde işlenmektedir.',
        sslProtected: 'SSL Koruması',
        encryption: '256-bit Şifreleme',
        monitoring: '7/24 İzleme'
      },
      passwordReset: {
        title: 'Şifre Sıfırla',
        description: 'E-posta adresinizi girin, size şifre sıfırlama bağlantısı gönderelim.',
        email: 'E-posta',
        emailLabel: 'E-posta Adresi',
        emailPlaceholder: 'ornek@VentraWind.com',
        sendLink: 'Bağlantı Gönder',
        sendButton: 'Yeni Şifre Gönder',
        sending: 'Gönderiliyor...',
        backToLogin: 'Giriş sayfasına dön',
        success: 'Şifre sıfırlama bağlantısı gönderildi',
        successTitle: 'Yeni Şifre Gönderildi!',
        successMessage: 'adresine yeni şifreniz gönderildi. Lütfen e-posta kutunuzu kontrol edin ve giriş yapmak için bu şifreyi kullanın.',
        warningMessage: '⚠️ E-posta gelmemişse lütfen spam/önemsiz klasörünüzü kontrol edin.',
        backToLoginButton: 'Giriş Sayfasına Dön',
        resendButton: 'Tekrar Gönder',
        error: 'Bir hata oluştu, lütfen tekrar deneyin',
        emailRequired: 'E-posta adresi gereklidir',
        emailInvalid: 'Geçerli bir e-posta adresi giriniz'
      }
    },
    
    dashboard: {
      title: 'Yönetim Paneli',
      turbines: 'Türbinler',
      users: 'Kullanıcılar',
      companies: 'Şirketler',
      reports: 'Raporlar',
      settings: 'Ayarlar',
      logout: 'Çıkış Yap',
      welcome: 'Hoş Geldiniz',
      totalTurbines: 'Toplam Türbin',
      activeTurbines: 'Aktif Türbin',
      totalReports: 'Toplam Rapor',
      systemHealth: 'Sistem Sağlığı',
      companyName: 'VentraWind Technology',
      turbineManagement: 'Türbin Yönetimi',
      addTurbine: 'Türbin Ekle',
      partnerManagement: 'Partner Yönetimi',
      userSettings: 'Kullanıcı Ayarları',
      searchPlaceholder: 'Türbin ara...',
      resultsFound: 'sonuç bulundu',
      noResults: 'Sonuç Bulunamadı',
      noResultsMessage: 'Arama kriterlerinize uygun türbin bulunamadı.',
      allCities: 'Tüm Şehirler',
      allYears: 'Tüm Yıllar',
      allCompanies: 'Tüm Şirketler',
      selectCompany: 'Şirket Seçin',
      pleaseSelectCompany: 'Lütfen bir şirket seçin',
      errorLoadingCompanies: 'Şirketler yüklenirken hata oluştu',
      errorLoadingTurbines: 'Türbinler yüklenirken hata oluştu',
      errorAddingTurbine: 'Türbin eklenirken hata oluştu',
      errorDeletingTurbine: 'Türbin silinirken hata oluştu',
      errorUpdatingTurbine: 'Türbin güncellenirken hata oluştu',
      apiError: 'API Hatası',
      active: 'Aktif',
      maintenance: 'Bakımda',
      faulty: 'Arızalı',
      offline: 'Çevrimdışı',
      inactive: 'Pasif',
      unknown: 'Bilinmiyor',
      error: 'Hata',
      turbineRooms: 'Blade',
      turbinePhotos2D: '2D Foto',
      turbinePhotos360: '360° Foto',
      photo: 'Fotoğraflar',
      addPhoto: 'Fotoğraf Ekle',
      view360: '360° Görünüm',
      edit: 'Düzenle',
      delete: 'Sil',
      addNewTurbine: 'Yeni Türbin Ekle',
      editTurbine: 'Türbin Düzenle',
      deleteTurbine: 'Türbin Sil',
      confirmDeleteMessage: 'Bu türbini silmek istediğinizden emin misiniz: {turbine}?',
      confirmAction: 'Bu işlem geri alınamaz.',
      deleteWarning1: 'Tüm fotoğraflar silinecek',
      deleteWarning2: 'Tüm raporlar silinecek',
      deleteWarning3: 'Tüm yorumlar silinecek',
      confirmDelete: 'Evet, Sil',
      cancel: 'İptal',
      save: 'Kaydet',
      add: 'Ekle',
      turbineName: 'Türbin Adı',
      turbineNamePlaceholder: 'Türbin adını girin',
      city: 'Şehir',
      cityName: 'Şehir Adı',
      cityPlaceholder: 'Şehir adını girin',
      company: 'Şirket',
      turbineCode: 'Türbin Kodu',
      codePlaceholder: 'Türbin kodunu girin',
      location: 'Konum',
      locationPlaceholder: 'Konum bilgisini girin'
    },
    
    turbine: {
      title: 'Türbin Yönetimi',
      name: 'Türbin Adı',
      location: 'Konum',
      status: 'Durum',
      createdAt: 'Oluşturulma Tarihi',
      actions: 'İşlemler',
      view: 'Görüntüle',
      edit: 'Düzenle',
      delete: 'Sil',
      add: 'Türbin Ekle',
      search: 'Türbin Ara...',
      filter: 'Filtrele',
      noData: 'Veri bulunamadı',
      loading: 'Türbinler yükleniyor...', // This will be replaced with t.turbinesLoading
      addNew: 'Yeni Türbin Ekle',
      editTurbine: 'Türbin Düzenle',
      deleteTurbine: 'Türbin Sil',
      confirmDelete: 'Bu türbini silmek istediğinizden emin misiniz?',
      deleteSuccess: 'Türbin başarıyla silindi',
      saveSuccess: 'Türbin başarıyla kaydedildi',
      rooms: 'Odalar',
      photos: 'Fotoğraflar',
      reports: 'Raporlar',
      details: 'Detaylar',
      roomCount: 'Oda Sayısı',
      updateRooms: 'Oda Sayısını Güncelle'
    },
    
    turbineDetail: {
      photos: {
        title: 'Fotoğraflar',
          bladePhotos: 'Kanat Fotoğrafları',
  bladeSelectionAndPhotos: 'Kanat Seçimi ve Fotoğrafları',
  bladeSelectionDescription: 'Her kanat için hangi odadan fotoğraf gösterileceğini seçin',
  dronePhotos: 'Drone Fotoğrafları',
  addPhoto: 'Fotoğraf Ekle',
        operatorPhotos: 'Operatör Fotoğrafları',
      selectPhoto: 'Fotoğraf seçin',
      selectPhotoDescription: 'Sol panelden bir fotoğraf seçerek görüntüleme işlemini başlatın.',
      noPhotosFound: 'Fotoğraf bulunamadı',
      backToTurbineDetail: 'Türbin Detay Ekranına Dön',
      zoomInstruction: 'Mouse tekerleği ile zoom yapabilirsiniz',
      robotPhotos: 'Robot Fotoğrafları',
        viewPhotos: 'Fotoğrafları Görüntüle',
        uploadPhotos: 'Fotoğraf Yükle',
        selectPhotos: 'Fotoğraf Seç',
        selectedPhotos: 'Seçilen Fotoğraflar',
        uploadButton: 'Yükle',
        uploading: 'Yükleniyor...',
        year: 'Yıl',
        allYears: 'Tüm Yıllar',
        search: 'Fotoğraf ara...',
        compare: 'Karşılaştır',
        cancelCompare: 'Karşılaştırmayı İptal Et',
        comparePhotos: 'Fotoğrafı Karşılaştır',
        month: 'Ay',
        photoCount: 'fotoğraf',
        uploader: 'Yükleyen',
        noPhotos: 'Henüz fotoğraf yok',
        noPhotosDescription: 'Bu türbin için henüz fotoğraf yüklenmemiş. İlk fotoğrafı yüklemek için Ekle butonunu kullanın.',
        openComments: 'Yorumları Aç',
        comments: 'Yorumlar',
        logout: 'Çıkış',
        // Zoom and annotation
        zoomIn: 'Yakınlaştır',
        zoomOut: 'Uzaklaştır',
        resetView: 'Görünümü Sıfırla',
        annotationMode: 'İşaretleme Modu',
        exitAnnotationMode: 'İşaretleme Modundan Çık',
        exitMode: 'Çık',
        addMarker: 'İşaret Ekle',
        hideMarkers: 'İşaretleri Gizle',
        showMarkers: 'İşaretleri Göster',
        deleteMarker: 'İşareti Sil',
        addMarkerLabel: 'İşaret Açıklaması Ekle',
        markerColor: 'Renk',
        markerPlaceholder: 'İşaret açıklaması girin...',
        save: 'Kaydet',
        clickToAddMarker: 'İşaret eklemek için fotoğrafa tıklayın',
        markers: 'işaret',
        // Version labels
        version: 'Versiyon',
        edit: 'Düzenleme',
        original: 'Orijinal',
      },
      rooms: {
        selectRoom: 'Oda Seç',
        room: 'Oda',
        editRooms: 'Odaları Düzenle',
        roomCount: 'Oda Sayısı',
        updateRoomCount: 'Oda Sayısını Güncelle'
      },
      reports: {
        title: 'Raporlar',
        generate: 'Rapor Oluştur',
        year: 'Yıl',
        existing: 'Mevcut Raporlar',
        noReports: 'Henüz rapor yok',
        download: 'İndir',
        delete: 'Sil',
        generating: 'Rapor oluşturuluyor...'
      },
      comments: {
        title: 'Yorumlar',
        add: 'Yorum Ekle',
        placeholder: 'Yorumunuzu yazın...',
        submit: 'Gönder',
        noComments: 'Henüz yorum yok',
        firstComment: 'İlk yorumu siz yapın!'
      }
    },
    
    users: {
      title: 'Kullanıcı Yönetimi',
      name: 'Ad Soyad',
      email: 'E-posta',
      role: 'Rol',
      company: 'Şirket',
      status: 'Durum',
      active: 'Aktif',
      inactive: 'Pasif',
      admin: 'Yönetici',
      operator: 'Operatör',
      superadmin: 'Süper Admin',
      customer: 'Müşteri',
      user: 'Kullanıcı',
      addUser: 'Kullanıcı Ekle',
      editUser: 'Kullanıcı Düzenle',
      deleteUser: 'Kullanıcı Sil',
      search: 'Kullanıcı ara...',
      filter: 'Filtrele'
    },
    
    companies: {
      title: 'Şirket Yönetimi',
      name: 'Şirket Adı',
      email: 'E-posta',
      phone: 'Telefon',
      address: 'Adres',
      userCount: 'Kullanıcı Sayısı',
      turbineCount: 'Türbin Sayısı',
      addCompany: 'Şirket Ekle',
      editCompany: 'Şirket Düzenle',
      deleteCompany: 'Şirket Sil',
      search: 'Şirket ara...'
    },
    
    support: {
      title: 'Destek',
      description: 'Size nasıl yardımcı olabiliriz?',
      contactUs: 'Bize Ulaşın',
      documentation: 'Dokümantasyon',
      tutorials: 'Eğitim Videoları'
    },

    privacy: {
      title: 'Gizlilik Politikası',
      description: 'Gizlilik politikamız ve veri koruma yaklaşımımız',
      lastUpdated: 'Son güncelleme'
    },

    blog: {
      sectionTitle: 'Teknoloji ve İnceleme Yazıları',
      sectionDescription: 'Rüzgar türbini inceleme teknolojileri ve yöntemleri hakkında detaylı bilgiler',
      previousPost: 'Önceki blog yazısı',
      nextPost: 'Sonraki blog yazısı',
      readMore: 'Devamını Oku',
      categoryTechnology: 'Teknoloji',
      categoryInspection: 'İnceleme',
      categoryEducation: 'Eğitim',
      categoryFieldWork: 'Saha Çalışması',
      videoLabel: 'Video',
      notFound: 'Blog yazısı bulunamadı',
      backToHome: 'Ana sayfaya dön',
      galleryTitle: 'Görsel Galeri',
      galleryImageAlt: 'Galeri',

      // Blog posts content
      posts: {
        fieldWorkNacelleRobot: {
          title: 'Sahadan: Bulutların Üzerinde Robotlu Kanat İncelemesi',
          subtitle: 'Ekibimizin nacelle üstünde yürüttüğü güncel türbin inceleme çalışması',
          date: '5 Eylül 2026',
          readTime: '6 dk okuma',
          heroTitle: 'Güncel Çalışmalarımız: Nacelle Üstünden Kanat İçine',
          introParagraph1: 'VentraWind ekibi, geçtiğimiz günlerde bulut seviyesinin üzerinde kalan bir rüzgar santralinde saha çalışması gerçekleştirdi. Türbin kulesine tırmanan teknisyenlerimiz, inceleme robotumuzu nacelle üstüne kadar taşıyarak kanat içi tarama operasyonunu başlattı. Fotoğraflarda görülen koşullar, ekibimizin sahada karşılaştığı gerçek çalışma ortamını yansıtmaktadır.',
          introParagraph2: 'Robotumuz, kompakt gövdesi ve arazi tipi tekerlekleri sayesinde tek bir teknisyen tarafından taşınabilmektedir. Bu sayede vinç, platform veya ek kaldırma ekipmanı kurulumuna gerek kalmadan, türbine ulaşan ekip incelemeye aynı gün içinde başlayabilir. Kurulum süresinin kısalması, türbinin duruş süresini ve dolayısıyla üretim kaybını doğrudan azaltır.',
          imageCaption1: 'Nacelle Üstünde Robot Yerleşimi',
          imageCaption2: 'Ekipman Taşıma',
          imageCaption3: 'Saha Ekibi',
          deploymentTitle: 'Robotun Kanata Yerleştirilmesi',
          deploymentParagraph1: 'Nacelle üstüne çıkan teknisyenimiz, robotu kanat içine göndermeden önce sensör kalibrasyonunu ve haberleşme testini sahada tamamlar. LiDAR ünitesi, 360° kamera modülü ve aydınlatma sistemi çalışır duruma getirildikten sonra robot kanat kök bölgesinden içeri bırakılır.',
          deploymentParagraph2: 'Kanat içindeki tarama boyunca robot, kat ettiği mesafeyi ve konumunu sürekli olarak kaydeder. Operatör ekibimiz kontrol noktasından canlı görüntüyü izler ve şüpheli bir bölge belirlendiğinde robotu durdurarak o noktada detaylı tarama yaptırır. Böylece kritik bulgular saha henüz terk edilmeden doğrulanır.',
          resultTitle: 'Çalışmanın Çıktıları',
          resultParagraph: 'Saha çalışmasında elde edilen LiDAR verisi, 360° görüntüler ve konum bilgisi VentraWind platformuna aktarılır. Türbin sahibi, her kanat için ayrı hazırlanan hasar haritasına, anomali listesine ve görsel kanıtlara aynı arayüz üzerinden erişir. Rapor, bakım planlaması için gereken öncelik sıralamasıyla birlikte teslim edilir.',
          conclusion: 'Bu çalışma, robotik inceleme sistemimizin gerçek saha koşullarındaki performansını bir kez daha doğruladı. Ekibimiz Türkiye genelindeki rüzgar santrallerinde benzer inceleme operasyonlarını sürdürüyor ve her sahadan edindiği veriyle sistemini geliştirmeye devam ediyor.'
        },
        robotLidarInspection: {
          title: 'LiDAR ve 360° Kamera Teknolojisi ile Türbin İçi İnceleme',
          subtitle: 'İnsanların ulaşamadığı noktalara robotik sistemlerle erişim',
          date: '15 Ocak 2026',
          readTime: '8 dk okuma',
          heroTitle: 'Rüzgar Türbinlerinde Yeni Nesil İnceleme Teknolojisi',
          introParagraph1: 'VentraWind olarak, rüzgar türbinlerinin iç yapısını incelemek için geliştirdiğimiz özel robotik sistemler, LiDAR sensörleri ve 360° kameralar ile donatılmıştır. Bu teknoloji sayesinde, insanların fiziksel olarak giremeyeceği veya güvenli bir şekilde çalışamayacağı türbin kanatlarının iç kısımlarına kadar ulaşabiliyoruz.',
          introParagraph2: 'LiDAR teknolojisi; milimetrik hassasiyetle 3D haritalama yapabilme, karanlık ortamlarda bile yüksek doğrulukta ölçüm alabilme, çatlak ve delaminasyon gibi yapısal hasarları tespit edebilme ve gerçek zamanlı veri aktarımı gibi önemli avantajlar sağlar.',
          anomalyTitle: 'Anomali Tespiti ve Konum Belirleme',
          anomalyParagraph1: 'Robotlarımız, türbin kanatlarının iç yüzeylerini tarayarak anomali tespiti yapar. Tespit edilen her hasar veya potansiyel risk noktası, koordinat sistemi üzerinde hassas bir şekilde işaretlenir. Bu sayede bakım ekipleri, sorunun tam konumunu bilir ve müdahale süresini minimize eder.',
          anomalyParagraph2: 'Yüksek çözünürlüklü 360° kameralarımız türbin içinin her açısını yakalarken, yapay zeka algoritmalarımız bu görüntüleri analiz ederek görsel anomalileri otomatik olarak tespit eder. Her inceleme sonrasında, tespit edilen anomalilerin konumu, boyutu ve risk seviyesini içeren kapsamlı bir rapor oluşturulur ve bu rapor 3D modeller ile görsel kanıtlarla desteklenir.',
          reportTitle: 'Rapor İçeriği',
          reportParagraph1: 'Müşterilerimize sunduğumuz detaylı raporlar; hasarın türbinin neresinde olduğunu, hasar tipini (çatlak, delaminasyon, erozyon vb.), boyut analizini ve risk değerlendirmesini içerir. Yüksek çözünürlüklü fotoğraflar, 360° görüntüler ve bakım önerileriyle desteklenen bu raporlar, türbin sahiplerine kapsamlı bir değerlendirme sunar.',
          conclusion: 'Bu kapsamlı yaklaşım sayesinde, türbin sahipleri ve operatörler, varlıklarının durumu hakkında tam bilgi sahibi olur ve proaktif bakım stratejileri geliştirebilir. Sonuç olarak, türbin ömrü uzar ve beklenmedik arıza süreleri minimize edilir.'
        },
        droneRobotInspection: {
          title: 'Robot ve Drone ile Kapsamlı Türbin İncelemesi',
          subtitle: 'Robot, drone ve operatör ile üç farklı perspektiften türbin hasar tespiti',
          date: '10 Ocak 2026',
          readTime: '10 dk okuma',
          heroTitle: 'Çok Katmanlı İnceleme Yaklaşımı',
          introParagraph: 'VentraWind\'in türbin inceleme metodolojisi, robot, drone ve uzman operatör ekiplerinin birlikte çalışmasına dayanır. Bu üçlü yaklaşım, türbinin her açıdan ve her derinlikte kapsamlı bir şekilde incelenmesini sağlar.',
          imageCaption1: 'Robot İncelemesi',
          imageCaption2: 'Drone Taraması',
          imageCaption3: 'Operatör Kontrolü',
          gridCaption: 'Robot ile kanat içi, drone ile dış yüzey, operatör ile kritik bileşen incelemesi',
          processParagraph: 'İnceleme sürecimiz, sistematik bir yaklaşımla türbinin her bileşenini kapsar. Öncelikle robot sistemleri kanat içine gönderilir, ardından drone ile dış yüzey taraması yapılır ve son olarak operatörler kritik noktaları fiziksel olarak kontrol eder.',
          conclusion: 'Bu kapsamlı inceleme metodolojisi sayesinde, türbinlerdeki potansiyel sorunlar erken aşamada tespit edilir ve büyük arızalar önlenir. Müşterilerimiz, türbinlerinin durumu hakkında tam şeffaflık ve güvenilir verilerle donatılmış raporlar alır.'
        },
        windTurbineTechnology: {
          title: 'Rüzgar Türbinleri: Çalışma Prensibi ve Türkiye\'deki Kullanım',
          subtitle: 'Rüzgar enerjisinin gücünden temiz elektrik üretimi',
          date: '5 Ocak 2026',
          readTime: '12 dk okuma',
          heroTitle: 'Rüzgar Enerjisi: Temiz ve Sürdürülebilir Güç Kaynağı',
          introParagraph: 'Rüzgar türbinleri, rüzgarın kinetik enerjisini elektrik enerjisine dönüştüren yenilenebilir enerji sistemleridir. Modern rüzgar türbinleri, 50-150 metre yüksekliğinde kuleler üzerinde, 40-80 metre çapında kanatlar ile donatılmıştır ve her biri 2-5 MW arası elektrik üretebilmektedir.',
          workingTitle: 'Rüzgar Türbinleri Nasıl Çalışır?',
          workingParagraph1: 'Rüzgar türbinlerinin çalışma prensibi basit ama etkilidir. Rüzgar, türbin kanatlarına çarptığında kanatlar dönmeye başlar. Bu dönme hareketi, türbin üstündeki nacelle içerisinde bulunan jeneratöre aktarılır. Jeneratör bu mekanik enerjiyi elektrik enerjisine dönüştürür.',
          workingParagraph2: 'Modern türbinler, rüzgar yönüne göre otomatik olarak yönlenebilir ve kanat açıları dinamik olarak ayarlanabilir. Bu sayede 3-25 m/s arasındaki rüzgar hızlarında optimum verimlilikle çalışabilirler. Türbinler, aşırı yüksek rüzgar hızlarında güvenlik için otomatik olarak durdurulur.',
          turkeyTitle: 'Türkiye\'de Rüzgar Enerjisi',
          turkeyParagraph: 'Türkiye, coğrafi konumu ve iklim özellikleri sayesinde yüksek rüzgar enerjisi potansiyeline sahiptir. Ülkemizde kurulu rüzgar enerjisi kapasitesi 11.000 MW seviyesini aşmış durumda ve bu kapasite her geçen yıl artmaktadır.',
          regionsTitle: 'Türkiye\'de En Çok Rüzgar Enerjisi Üretilen Bölgeler',
          region1Name: 'İzmir - Çeşme, Karaburun, Bergama',
          region1Desc: 'Ege Bölgesi\'nin en yüksek potansiyeli',
          region2Name: 'Balıkesir - Bandırma, Gönen',
          region2Desc: 'Marmara\'nın önemli rüzgar koridoru',
          region3Name: 'Çanakkale - Bozcaada, Gelibolu',
          region3Desc: 'Deniz ve kara rüzgarlarının buluşması',
          region4Name: 'Hatay - İskenderun',
          region4Desc: 'Akdeniz\'in güçlü rüzgar koridoru',
          region5Name: 'Osmaniye - Bahçe',
          region5Desc: 'Güney Anadolu\'nun yüksek potansiyeli',
          region6Name: 'Yalova - Çınarcık',
          region6Desc: 'Marmara Denizi kıyı bölgesi',
          region7Name: 'Manisa - Akhisar, Soma',
          region7Desc: 'İç Ege\'nin rüzgar potansiyeli',
          region8Name: 'Afyonkarahisar - Sandıklı',
          region8Desc: 'İç Anadolu\'nun yüksek rakımlı bölgeleri',
          advantagesTitle: 'Rüzgar Enerjisinin Avantajları',
          advantagesParagraph: 'Rüzgar enerjisi tamamen temiz ve yenilenebilir bir enerji kaynağıdır. Atmosfere zararlı gaz salınımı yapmaz, su tüketimi gerektirmez ve yakıt maliyeti yoktur. Bir rüzgar türbini kurulduktan sonra 20-25 yıl boyunca kesintisiz elektrik üretebilir.',
          conclusion: 'VentraWind olarak, bu temiz enerji kaynaklarının verimli ve güvenli çalışmasını sağlamak için gelişmiş inceleme ve bakım teknolojileri sunuyoruz. Türbinlerinizin maksimum performansta çalışmasını ve uzun ömürlü olmasını sağlıyoruz.'
        },
        worldsBiggestTurbine: {
          title: 'Dünyanın En Büyük Rüzgar Türbini',
          subtitle: '16 MW güç kapasitesi ile yeni bir çağ',
          date: '20 Ocak 2026',
          readTime: '7 dk okuma',
          heroTitle: 'Dev Türbinler: Rüzgar Enerjisinin Geleceği',
          introParagraph: 'Rüzgar enerjisi teknolojisi son yıllarda inanılmaz bir gelişim gösterdi. Dünyanın en büyük rüzgar türbini 16 MW güç kapasitesine ulaşarak, tek başına 20.000 hanenin elektrik ihtiyacını karşılayabilecek seviyeye geldi. Bu dev yapılar, 260 metre yüksekliğe ve 123 metre uzunluğunda kanatlara sahiptir.',
          specTitle: 'Teknik Özellikler',
          specParagraph: 'Modern mega türbinler, rotor çapı 240 metreyi aşan devasa yapılardır. Kanatların uç hızı 350 km/s\'ye ulaşabilir ve tam kapasitede çalıştığında saatte 80.000 kWh elektrik üretebilir. Bu türbinlerin her biri, yılda ortalama 80 GWh enerji üretimi gerçekleştirebilir.',
          powerTitle: 'Enerji Üretim Kapasitesi',
          powerParagraph: 'Bir 16 MW\'lık türbin, yılda yaklaşık 20.000 evin elektrik ihtiyacını karşılayabilir. Bu kapasite, geleneksel 2 MW\'lık türbinlere kıyasla 8 kat daha fazla enerji üretimi demektir. Ayrıca, daha az sayıda türbin kurulması sayesinde çevre üzerindeki etki minimize edilir.',
          futureTitle: 'Geleceğin Teknolojisi',
          futureParagraph: 'Üreticiler, önümüzdeki yıllarda 20 MW ve üzeri kapasiteli türbinler geliştirmeyi hedefliyor. Yapay zeka destekli kontrol sistemleri, gelişmiş malzeme teknolojileri ve optimize edilmiş kanat tasarımları sayesinde verimlilik sürekli artıyor.',
          conclusion: 'VentraWind olarak, bu dev türbinlerin inceleme ve bakımında da en gelişmiş teknolojileri kullanıyoruz. Robotik sistemler, drone teknolojisi ve uzman ekiplerimizle bu devasa yapıların güvenli ve verimli çalışmasını sağlıyoruz.'
        },
        ropeAccessTurbine: {
          title: 'Rüzgar Türbinlerinde İple Erişim Teknikleri',
          subtitle: 'Yüksekte güvenli çalışma ve etkili bakım',
          date: '18 Ocak 2026',
          readTime: '9 dk okuma',
          heroTitle: 'İple Erişim: Türbin Bakımında Kritik Yöntem',
          introParagraph: 'Rüzgar türbinlerinin bakım ve onarımında iple erişim (rope access) teknikleri, en kritik yöntemlerden biridir. 100 metreyi aşan yüksekliklerde çalışabilen uzman teknisyenlerimiz, özel eğitim ve sertifikalarla donatılmış olup, türbin kanatlarının dış yüzeylerinde güvenli bir şekilde çalışabilmektedir.',
          safetyTitle: 'Güvenlik ve Eğitim',
          safetyParagraph: 'İple erişim teknisyenleri, IRATA (Industrial Rope Access Trade Association) veya eşdeğer uluslararası standartlarda eğitim alır. Her teknisyen, çift emniyet sistemi, düşme önleyici ekipmanlar ve acil durum prosedürleri konusunda kapsamlı eğitime sahiptir. Tüm çalışmalar, rüzgar hızı ve hava koşulları dikkatle değerlendirilerek gerçekleştirilir.',
          advantagesTitle: 'İple Erişimin Avantajları',
          advantage1: 'Hızlı Müdahale: Platform veya vinç kurulumuna gerek kalmadan hızlı şekilde türbine erişim sağlanır.',
          advantage2: 'Maliyet Etkinliği: Ağır ekipman ve araç gerektirmediği için bakım maliyetleri düşer.',
          advantage3: 'Esneklik: Her türlü hava koşulunda ve türbin modelinde çalışma imkanı sunar.',
          advantage4: 'Detaylı İnceleme: Teknisyenler, türbin yüzeyinin her noktasını yakından inceleyebilir ve küçük hasarları bile tespit edebilir.',
          conclusion: 'VentraWind olarak, deneyimli iple erişim ekibimizle türbinlerinizin dış yüzey bakımını, hasar onarımını ve rutin kontrollerini güvenli bir şekilde gerçekleştiriyoruz. Uzman teknisyenlerimiz, yüksek standartlarda eğitim almış ve uluslararası sertifikalara sahiptir.'
        },
        offshoreTurbines: {
          title: 'Offshore Rüzgar Türbinleri: Denizlerin Enerji Potansiyeli',
          subtitle: 'Açık denizlerde yüksek kapasiteli enerji üretimi',
          date: '22 Ocak 2026',
          readTime: '10 dk okuma',
          heroTitle: 'Offshore Rüzgar Enerjisi: Sınırsız Potansiyel',
          introParagraph: 'Offshore (açık deniz) rüzgar türbinleri, karada kurulan türbinlere göre çok daha yüksek enerji üretim potansiyeline sahiptir. Deniz üzerinde rüzgar hızları daha yüksek ve daha süreklidir. Modern offshore türbinler, sabit tabanlı (fixed-bottom) veya yüzen (floating) platformlar üzerine kurulabilmektedir.',
          advantagesTitle: 'Offshore Türbinlerin Avantajları',
          advantagesParagraph: 'Offshore türbinler, karadaki türbinlere göre %40-50 daha fazla enerji üretebilir. Denizde rüzgar engelleri olmadığı için türbinler daha verimli çalışır. Ayrıca, görsel ve gürültü kirliliği açısından yerleşim alanlarından uzak olmaları büyük avantaj sağlar. Offshore rüzgar çiftlikleri, binlerce megawatt kapasiteye ulaşabilir.',
          challengesTitle: 'Zorluklar ve Çözümler',
          challengesParagraph: 'Offshore türbinlerin kurulumu ve bakımı, karadaki türbinlere göre daha maliyetli ve teknik olarak zorlayıcıdır. Tuzlu su korozyonu, dalga ve akıntı etkisi, zorlu hava koşulları gibi faktörler göz önünde bulundurulmalıdır. Ancak gelişen teknoloji ile bu zorluklar aşılmakta ve offshore rüzgar enerjisi giderek daha ekonomik hale gelmektedir.',
          turkeyTitle: 'Türkiye\'de Offshore Rüzgar Potansiyeli',
          turkeyParagraph: 'Türkiye, üç tarafı denizlerle çevrili olması sayesinde yüksek offshore rüzgar potansiyeline sahiptir. Özellikle Ege Denizi, Marmara ve Karadeniz kıyılarında offshore rüzgar projeleri planlanmaktadır. İlk offshore rüzgar çiftlikleri ile Türkiye, yenilenebilir enerji kapasitesini önemli ölçüde artırabilecektir.',
          conclusion: 'VentraWind olarak, offshore rüzgar türbinlerinin inceleme ve bakımında uzmanlaşmış ekibimizle, bu zorlayıcı ortamlarda bile en yüksek standartlarda hizmet sunmayı hedefliyoruz. Deniz koşullarına uygun ekipman ve deneyimli personelimizle offshore projelerinize destek oluyoruz.'
        }
      }
    },

    ui: {
      videoLabel: 'Video',
      robotInspection: 'Robot İncelemesi',
      goToMedia: 'Medyaya git',
      turbine: 'Türbin'
    },

    robot: {
      hero: {
        advancedTech: 'İleri Teknoloji',
        liveInspection: 'Canlı İnceleme',
        expertTeam: 'Uzman Ekip',
        precisionScanning: 'Hassas Tarama',
        controlCenter: 'Kontrol Merkezi',
        damageAnalysis: 'Hasar Analizi',
        techTitle: 'Robotik İnceleme Teknolojisi',
        inspectionTitle: 'Türbin İçi Robot İncelemesi',
        teamTitle: 'Uzman Operatör Ekibi',
        processTitle: 'Detaylı İnceleme Süreci',
        centerTitle: 'Modern Kontrol Merkezi',
        bladeTitle: 'Kanat İçi İnceleme',
        techDesc: 'İleri teknoloji robot sistemlerimizle türbin içi detaylı inceleme ve bakım hizmetleri sunuyoruz.',
        inspectionDesc: 'Robotlarımız türbin içinde hassas inceleme ve veri toplama işlemlerini gerçekleştiriyor.',
        teamDesc: 'Deneyimli operatör ekibimiz robot sistemlerini uzaktan kontrol eder ve analiz yapar.',
        processDesc: 'Robot sistemlerimiz türbin bileşenlerini milimetrik hassasiyetle tarayarak hasar tespiti yapar.',
        centerDesc: 'Modern kontrol merkezimizde tüm robot operasyonları izlenir ve yönetilir.',
        bladeDesc: 'Türbin kanatlarının iç yüzeylerinde yüksek çözünürlüklü görüntüleme ve hasar analizi.'
      },
      damage: {
        badge: 'Tespit Örnekleri',
        title: 'Hasar Tespiti',
        description: 'Robot teknolojimiz ile yapılan hassas hasar tespiti örnekleri',
        detected: 'Hasar Tespit Edildi',
        detection1: 'Hasar Tespiti - 1',
        detection2: 'Hasar Tespiti - 2',
        detection3: 'Hasar Tespiti - 3',
        detection4: 'Hasar Tespiti - 4'
      },
      gallery: {
        badge: 'Fotoğraf Arşivi',
        title: 'Galeri',
        description: 'Türbin inceleme ve bakım süreçlerimizden görüntüler',
        robotOp: 'Robot Operasyonu',
        equipment: 'Teknik Ekipman',
        interior: 'Türbin İç Görünüm',
        upper: 'Türbin Üst Kısım',
        platform: 'Üst Platform',
        general: 'Türbin Genel Görünüm',
        control: 'Kontrol Odası',
        bladeInspection: 'Robot Kanat İçi İnceleme',
        detailedInspection: 'Kanat İçi Detaylı İnceleme',
        bladeScanning: 'Kanat İçi Tarama',
        blade: 'Türbin Kanadı',
        surface: 'Kanat Dış Yüzey'
      }
    },
    
    errors: {
      notFound: 'Sayfa bulunamadı',
      serverError: 'Sunucu hatası',
      unauthorized: 'Bu sayfaya erişim yetkiniz yok',
      networkError: 'Ağ bağlantı hatası',
      tryAgain: 'Tekrar Dene',
      goHome: 'Ana Sayfaya Dön',
      title: 'Hata',
      generic: 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.',
      network: 'Sunucuya ulaşılamıyor. İnternet bağlantınızı kontrol edip tekrar deneyin.',
      timeout: 'İşlem zaman aşımına uğradı. Lütfen tekrar deneyin.',
      sessionExpired: 'Oturumunuzun süresi doldu. Lütfen tekrar giriş yapın.',
      permission: 'Bu işlem için yetkiniz yok.',
      validation: 'Girdiğiniz bilgilerde bir sorun var. Lütfen kontrol edip tekrar deneyin.',
      conflict: 'Bu kayıt zaten mevcut.',
      rateLimit: 'Çok fazla istek gönderildi. Lütfen biraz bekleyip tekrar deneyin.',
      server: 'Sunucuda bir hata oluştu. Lütfen daha sonra tekrar deneyin.',
      notFoundResource: 'Aradığınız kayıt bulunamadı.',
      loadFailed: 'Veriler yüklenemedi.',
      retry: 'Tekrar dene',
      dismiss: 'Kapat',
      fileTooLarge: 'Dosya çok büyük. Lütfen daha küçük bir dosya seçin.',
      invalidFileType: 'Bu dosya biçimi kabul edilmiyor.',
      partialUploadTitle: 'Bazı dosyalar yüklenemedi',
      partialUploadSuffix: 'dosya yüklenemedi',
      renderCrashTitle: 'Beklenmeyen bir hata oluştu',
      renderCrashBody: 'Bu bölüm görüntülenirken bir sorun çıktı. Sayfayı yenilemeyi deneyin.',
      reloadPage: 'Sayfayı yenile'
    }
  },
  
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      contact: 'Contact',
      login: 'Login'
    },
    
    footer: {
      company: 'Company',
      support: 'Support',
      about: 'About',
      contact: 'Contact',
      support_page: 'Support',
      privacy: 'Privacy',
      description: 'We digitize your business processes with modern technologies and provide scalable and secure solutions.',
      address: 'İZMİR/TÜRKİYE',
      fullAddress: 'VentraWind Teknoloji A.Ş., Adalet Mahallesi, Manas Bulvarı No: 47/A, Folkart Towers A Kule Kat: 12, 35535 Bayraklı/İZMİR',
      email: 'info@ventrawind.com',
      phone: '0 534 495 25 90'
    },
    
    common: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      cancel: 'Cancel',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      search: 'Search',
      filter: 'Filter',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      close: 'Close',
      confirm: 'Confirm',
      yes: 'Yes',
      no: 'No',
      add: 'Add',
      remove: 'Remove',
      select: 'Select'
    },

    // Comment related (root level)
    confirmDeleteComment: 'Are you sure you want to delete this comment?',
    noCommentsYet: 'No comments yet',
    editComment: 'Edit comment',
    deleteComment: 'Delete comment',
    saving: 'Saving...',
    save: 'Save',
    cancel: 'Cancel',
    showLess: 'Show less',
    readMore: 'Read more',
    writeComment: 'Write a comment...',
    turbineNotFound: 'Turbine not found',
    backToDashboard: 'Back to Dashboard',
    selectPhoto: 'Select photo',
    noPhotosFound: 'No photos found',

    loading: {
      loadingTurbineData: 'Loading Turbine Data',
      dataPreparation: 'Data is being prepared, please wait...',
      panoramicViewLoading: 'Preparing Panoramic View',
      turbineDataAndPhotosLoading: 'Loading turbine data and photos...',
      deepwindPreparing: 'VentraWind Preparing',
      authAndDataLoading: 'Authenticating and loading data...',
      photosLoading: 'Loading photos...',
      reportsLoading: 'Loading reports...',
      companiesLoading: 'Loading companies...',
      usersLoading: 'Loading users...',
      turbinesLoading: 'Loading turbines...',
      goBack: 'Go Back'
    },
    
    home: {
      title: 'VentraWind Technology',
      subtitle: 'Professional Technology Solutions',
      description: 'We digitize your business processes with modern technologies and provide scalable and secure solutions.',
      heroTitle: 'Next Generation',
      heroSubtitle: 'Wind Energy Monitoring',
      heroDescription: 'Maximize your turbine performance with 360° imaging, AI-powered analysis and real-time monitoring.',
      getStarted: 'Get Started',
      learnMore: 'Learn More',
      features: {
        title: 'Features',
        subtitle: 'We are here to provide you with the best service'
      },
      partnerships: {
        title: 'Our Partners',
        subtitle: 'We achieve success together with our trusted business partners'
      },
      technologies: {
        title: 'Our Technologies',
        subtitle: 'We develop smart solutions with the most advanced technologies',
        ai: 'Artificial Intelligence',
        machineLearning: 'Machine Learning',
        iotSensors: 'IoT Sensors',
        cloudComputing: 'Cloud Computing',
        view360: '360° Viewing',
        predictiveAnalytics: 'Predictive Analytics',
        realTimeMonitoring: 'Real-time Monitoring',
        bigData: 'Big Data',
        computerVision: 'Computer Vision',
        edgeDetection: 'Edge Detection',
        damageDetection: 'Damage Detection',
        anomalyDetection: 'Anomaly Detection'
      }
    },
    
    about: {
      title: 'About Us',
      description: 'As VentraWind, we provide innovative solutions in the field of technology.',
      mission: 'Our Mission',
      vision: 'Our Vision',
      values: {
      sustainability: 'Sustainability',
      innovation: 'Innovation',
      efficiency: 'Efficiency',
      quality: 'Quality',
      sustainabilityDesc: 'We develop environmentally friendly technologies for a clean energy future.',
      innovationDesc: 'We continue to be pioneers in the industry with continuous research and development.',
      efficiencyDesc: 'We maximize our customers\' energy production efficiency.',
      qualityDesc: 'We provide quality service at international standards.'
    },
      heroDescription: 'Since 2018, we have been contributing to a sustainable energy future with innovative technologies in the wind energy sector.',
      storyTitle: 'Our Story',
      storyParagraph1: 'VentraWind, established in Izmir as a technology company, specializes in monitoring and management of wind energy facilities.',
      storyParagraph2: 'We develop 360° imaging technology, AI-powered analysis systems and real-time monitoring solutions to evaluate Türkiye\'s wind energy potential most efficiently.',
      storyParagraph3: 'Today, our team responsible for monitoring more than 150 wind turbines continues to provide innovative solutions in the industry with continuous research and development activities.',
      turbineCountTitle: '150+ Turbines Monitored',
      turbineCountDescription: 'We monitor and optimize wind turbines located in different geographical regions throughout Türkiye 24/7.',
      missionTitle: 'Our Mission',
      missionDescription: 'To increase energy production efficiency, reduce maintenance costs and contribute to a sustainable energy future by using the most advanced technologies in wind energy facilities.',
      visionTitle: 'Our Vision',
      visionDescription: 'To become the leading company in wind energy monitoring technologies in Türkiye and the region, and to become a globally recognized brand with AI-powered solutions.',
      valuesTitle: 'Our Values',
      valuesSubtitle: 'The core values that define how we do business',
      ctaTitle: 'Contact Us',
      ctaDescription: 'Learn how we can support your wind energy projects.',
      ctaButton: 'Get In Touch',
      storyLabel: 'Our Story',
      turbineLabel: 'Turbines'
    },
    
    contact: {
      title: 'Contact',
      description: 'We are here to discuss your projects or answer your questions. Contact us and let\'s work together.',
      name: 'Full Name',
      nameRequired: 'Full Name *',
      namePlaceholder: 'Enter your name and surname',
      email: 'Email',
      phone: 'Phone',
      phonePlaceholder: 'Enter your phone number',
      subject: 'Subject',
      subjectRequired: 'Subject *',
      subjectPlaceholder: 'Enter the subject of your message',
      message: 'Message',
      messageRequired: 'Message *',
      messagePlaceholder: 'Write your message here...',
      send: 'Send Message',
      sending: 'Sending...',
      success: 'Your message has been sent successfully!',
      error: 'An error occurred while sending the message',
      address: 'Address',
      workingHours: 'Working Hours',
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday', 
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday',
      closed: 'Closed',
      sendMessage: 'Send Message'
    },
    
    auth: {
      login: {
        title: 'Login',
        email: 'Email',
        password: 'Password',
        emailLabel: 'Email Address',
        emailPlaceholder: 'example@email.com',
        passwordLabel: 'Password',
        passwordPlaceholder: '••••••••',
        loginButton: 'Login',
        loggingIn: 'Logging in...',
        forgotPassword: 'Forgot Password',
        resetPassword: 'Reset Password',
        noAccount: 'Don\'t have an account?',
        register: 'Sign Up',
        loginError: 'Login failed. Please check your credentials.',
        welcome: 'Welcome',
        subtitle: 'Sign in to your account',
        securityNotice: 'Security Notice',
        securityText: 'Your data is protected with 256-bit SSL encryption. All login attempts are monitored and processed according to our security protocols.',
        sslProtected: 'SSL Protected',
        encryption: '256-bit Encryption',
        monitoring: '24/7 Monitoring'
      },
      passwordReset: {
        title: 'Reset Password',
        description: 'Enter your email address and we\'ll send you a password reset link.',
        email: 'Email',
        emailLabel: 'Email Address',
        emailPlaceholder: 'example@VentraWind.com',
        sendLink: 'Send Link',
        sendButton: 'Send New Password',
        sending: 'Sending...',
        backToLogin: 'Back to login page',
        success: 'Password reset link sent',
        successTitle: 'New Password Sent!',
        successMessage: 'A new password has been sent to your email address. Please check your inbox and use this password to log in.',
        warningMessage: '⚠️ If you don\'t receive the email, please check your spam/junk folder.',
        backToLoginButton: 'Back to Login',
        resendButton: 'Send Again',
        error: 'An error occurred, please try again',
        emailRequired: 'Email address is required',
        emailInvalid: 'Please enter a valid email address'
      }
    },
    
    dashboard: {
      title: 'Dashboard',
      turbines: 'Turbines',
      users: 'Users',
      companies: 'Companies',
      reports: 'Reports',
      settings: 'Settings',
      logout: 'Logout',
      welcome: 'Welcome',
      totalTurbines: 'Total Turbines',
      activeTurbines: 'Active Turbines',
      totalReports: 'Total Reports',
      systemHealth: 'System Health',
      companyName: 'VentraWind Technology',
      turbineManagement: 'Turbine Management',
      addTurbine: 'Add Turbine',
      partnerManagement: 'Partner Management',
      userSettings: 'User Settings',
      searchPlaceholder: 'Search turbines...',
      resultsFound: 'results found',
      noResults: 'No Results Found',
      noResultsMessage: 'No turbines found matching your search criteria.',
      allCities: 'All Cities',
      allYears: 'All Years',
      allCompanies: 'All Companies',
      selectCompany: 'Select Company',
      pleaseSelectCompany: 'Please select a company',
      errorLoadingCompanies: 'Error loading companies',
      errorLoadingTurbines: 'Error loading turbines',
      errorAddingTurbine: 'Error adding turbine',
      errorDeletingTurbine: 'Error deleting turbine',
      errorUpdatingTurbine: 'Error updating turbine',
      apiError: 'API Error',
      active: 'Active',
      maintenance: 'Maintenance',
      faulty: 'Faulty',
      offline: 'Offline',
      inactive: 'Inactive',
      unknown: 'Unknown',
      error: 'Error',
      turbineRooms: 'Blades',
      turbinePhotos2D: '2D Photos',
      turbinePhotos360: '360° Photos',
      photo: 'Photos',
      addPhoto: 'Add Photo',
      view360: '360° View',
      edit: 'Edit',
      delete: 'Delete',
      addNewTurbine: 'Add New Turbine',
      editTurbine: 'Edit Turbine',
      deleteTurbine: 'Delete Turbine',
      confirmDeleteMessage: 'Are you sure you want to delete this turbine: {turbine}?',
      confirmAction: 'This action cannot be undone.',
      deleteWarning1: 'All photos will be deleted',
      deleteWarning2: 'All reports will be deleted',
      deleteWarning3: 'All comments will be deleted',
      confirmDelete: 'Yes, Delete',
      cancel: 'Cancel',
      save: 'Save',
      add: 'Add',
      turbineName: 'Turbine Name',
      turbineNamePlaceholder: 'Enter turbine name',
      city: 'City',
      cityName: 'City Name',
      cityPlaceholder: 'Enter city name',
      company: 'Company',
      turbineCode: 'Turbine Code',
      codePlaceholder: 'Enter turbine code',
      location: 'Location',
      locationPlaceholder: 'Enter location information'
    },
    
    turbine: {
      title: 'Turbine Management',
      name: 'Turbine Name',
      location: 'Location',
      status: 'Status',
      createdAt: 'Created At',
      actions: 'Actions',
      view: 'View',
      edit: 'Edit',
      delete: 'Delete',
      add: 'Add Turbine',
      search: 'Search Turbines...',
      filter: 'Filter',
      noData: 'No data found',
      loading: 'Loading turbines...',
      addNew: 'Add New Turbine',
      editTurbine: 'Edit Turbine',
      deleteTurbine: 'Delete Turbine',
      confirmDelete: 'Are you sure you want to delete this turbine?',
      deleteSuccess: 'Turbine deleted successfully',
      saveSuccess: 'Turbine saved successfully',
      rooms: 'Rooms',
      photos: 'Photos',
      reports: 'Reports',
      details: 'Details',
      roomCount: 'Room Count',
      updateRooms: 'Update Room Count'
    },
    
    turbineDetail: {
      photos: {
        title: 'Photos',
        bladePhotos: 'Blade Photos',
        bladeSelectionAndPhotos: 'Blade Selection and Photos',
        bladeSelectionDescription: 'Select which room photos to display for each blade',
        dronePhotos: 'Drone Photos',
        addPhoto: 'Add Photo',
        operatorPhotos: 'Operator Photos',
      selectPhoto: 'Select photo',
      selectPhotoDescription: 'Select a photo from the left panel to start viewing.',
      noPhotosFound: 'No photos found',
      backToTurbineDetail: 'Back to Turbine Detail Screen',
      zoomInstruction: 'Use mouse wheel to zoom',
      robotPhotos: 'Robot Photos',
        viewPhotos: 'View Photos',
        uploadPhotos: 'Upload Photos',
        selectPhotos: 'Select Photos',
        selectedPhotos: 'Selected Photos',
        uploadButton: 'Upload',
        uploading: 'Uploading...',
        year: 'Year',
        allYears: 'All Years',
        search: 'Search photos...',
        compare: 'Compare',
        cancelCompare: 'Cancel Compare',
        comparePhotos: 'Compare Photos',
        month: 'Month',
        photoCount: 'photos',
        uploader: 'Uploader',
        noPhotos: 'No photos yet',
        noPhotosDescription: 'No photos have been uploaded for this turbine yet. Use the Add button to upload the first photo.',
        openComments: 'Open Comments',
        comments: 'Comments',
        logout: 'Logout',
        // Zoom and annotation
        zoomIn: 'Zoom In',
        zoomOut: 'Zoom Out',
        resetView: 'Reset View',
        annotationMode: 'Annotation Mode',
        exitAnnotationMode: 'Exit Annotation Mode',
        exitMode: 'Exit',
        addMarker: 'Add Marker',
        hideMarkers: 'Hide Markers',
        showMarkers: 'Show Markers',
        deleteMarker: 'Delete Marker',
        addMarkerLabel: 'Add Marker Label',
        markerColor: 'Color',
        markerPlaceholder: 'Enter marker description...',
        save: 'Save',
        clickToAddMarker: 'Click on the image to add a marker',
        markers: 'marker(s)',
        // Version labels
        version: 'Version',
        edit: 'Edit',
        original: 'Original',
      },
      rooms: {
        selectRoom: 'Select Room',
        room: 'Room',
        editRooms: 'Edit Rooms',
        roomCount: 'Room Count',
        updateRoomCount: 'Update Room Count'
      },
      reports: {
        title: 'Reports',
        generate: 'Generate Report',
        year: 'Year',
        existing: 'Existing Reports',
        noReports: 'No reports yet',
        download: 'Download',
        delete: 'Delete',
        generating: 'Generating report...'
      },
      comments: {
        title: 'Comments',
        add: 'Add Comment',
        placeholder: 'Write your comment...',
        submit: 'Submit',
        noComments: 'No comments yet',
        firstComment: 'Be the first to comment!'
      }
    },
    
    users: {
      title: 'User Management',
      name: 'Full Name',
      email: 'Email',
      role: 'Role',
      company: 'Company',
      status: 'Status',
      active: 'Active',
      inactive: 'Inactive',
      admin: 'Admin',
      operator: 'Operator',
      superadmin: 'Super Admin',
      customer: 'Customer',
      user: 'User',
      addUser: 'Add User',
      editUser: 'Edit User',
      deleteUser: 'Delete User',
      search: 'Search users...',
      filter: 'Filter'
    },
    
    companies: {
      title: 'Company Management',
      name: 'Company Name',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      userCount: 'User Count',
      turbineCount: 'Turbine Count',
      addCompany: 'Add Company',
      editCompany: 'Edit Company',
      deleteCompany: 'Delete Company',
      search: 'Search companies...'
    },
    
    support: {
      title: 'Support',
      description: 'How can we help you?',
      contactUs: 'Contact Us',
      documentation: 'Documentation',
      tutorials: 'Tutorials'
    },

    privacy: {
      title: 'Privacy Policy',
      description: 'Our privacy policy and data protection approach',
      lastUpdated: 'Last updated'
    },

    blog: {
      sectionTitle: 'Technology and Inspection Articles',
      sectionDescription: 'Detailed information about wind turbine inspection technologies and methods',
      previousPost: 'Previous blog post',
      nextPost: 'Next blog post',
      readMore: 'Read More',
      categoryTechnology: 'Technology',
      categoryInspection: 'Inspection',
      categoryEducation: 'Education',
      categoryFieldWork: 'Field Work',
      videoLabel: 'Video',
      notFound: 'Blog post not found',
      backToHome: 'Back to home',
      galleryTitle: 'Visual Gallery',
      galleryImageAlt: 'Gallery',

      // Blog posts content
      posts: {
        fieldWorkNacelleRobot: {
          title: 'From the Field: Robotic Blade Inspection Above the Clouds',
          subtitle: 'Our team\'s latest turbine inspection campaign carried out on top of the nacelle',
          date: 'September 5, 2026',
          readTime: '6 min read',
          heroTitle: 'Our Latest Work: From the Nacelle Roof into the Blade',
          introParagraph1: 'The VentraWind team recently completed a field campaign at a wind farm located above the cloud line. Our technicians climbed the turbine tower, carried our inspection robot all the way to the nacelle roof and started the internal blade scanning operation. The conditions in these photographs reflect the real working environment our team faces in the field.',
          introParagraph2: 'Thanks to its compact body and all-terrain wheels, our robot can be carried by a single technician. This means no crane, platform or additional lifting equipment has to be installed, so the team can begin the inspection on the same day it reaches the turbine. Shorter setup time directly reduces turbine downtime and the resulting production loss.',
          imageCaption1: 'Robot Deployment on the Nacelle',
          imageCaption2: 'Carrying the Equipment',
          imageCaption3: 'Field Team',
          deploymentTitle: 'Placing the Robot into the Blade',
          deploymentParagraph1: 'Before sending the robot into the blade, our technician on the nacelle roof completes the sensor calibration and communication test on site. Once the LiDAR unit, the 360° camera module and the lighting system are operational, the robot is released into the blade through the root section.',
          deploymentParagraph2: 'Throughout the scan inside the blade, the robot continuously records the distance travelled and its position. Our operator team follows the live feed from the control point and, whenever a suspicious area is identified, stops the robot to perform a detailed scan at that spot. Critical findings are therefore verified before the site is left.',
          resultTitle: 'Outputs of the Campaign',
          resultParagraph: 'The LiDAR data, 360° imagery and position information collected in the field are transferred to the VentraWind platform. The turbine owner accesses the damage map, the anomaly list and the visual evidence prepared separately for each blade through the same interface. The report is delivered together with the priority ranking required for maintenance planning.',
          conclusion: 'This campaign once again confirmed the performance of our robotic inspection system under real field conditions. Our team continues to run similar inspection operations at wind farms across Türkiye and keeps improving the system with the data gathered from every site.'
        },
        robotLidarInspection: {
          title: 'LiDAR and 360° Camera Technology for Turbine Interior Inspection',
          subtitle: 'Accessing areas inaccessible to humans with robotic systems',
          date: 'January 15, 2026',
          readTime: '8 min read',
          heroTitle: 'Next Generation Inspection Technology in Wind Turbines',
          introParagraph1: 'As VentraWind, our specially developed robotic systems for inspecting the internal structure of wind turbines are equipped with LiDAR sensors and 360° cameras. Thanks to this technology, we can reach the interior surfaces of turbine blades that humans cannot physically enter or work safely in.',
          introParagraph2: 'LiDAR technology provides important advantages such as 3D mapping with millimeter precision, high accuracy measurements even in dark environments, detection of structural damages such as cracks and delamination, and real-time data transmission.',
          anomalyTitle: 'Anomaly Detection and Location Determination',
          anomalyParagraph1: 'Our robots perform anomaly detection by scanning the inner surfaces of turbine blades. Each detected damage or potential risk point is precisely marked on the coordinate system. This allows maintenance teams to know the exact location of the problem and minimize intervention time.',
          anomalyParagraph2: 'Our high-resolution 360° cameras capture every angle inside the turbine, while our AI algorithms analyze these images to automatically detect visual anomalies. After each inspection, a comprehensive report is created containing the location, size, and risk level of detected anomalies, and this report is supported with visual evidence using 3D models.',
          reportTitle: 'Report Content',
          reportParagraph1: 'The detailed reports we provide to our customers include where the damage is in the turbine, the type of damage (cracks, delamination, erosion, etc.), size analysis, and risk assessment. These reports, supported by high-resolution photos, 360° images, and maintenance recommendations, provide turbine owners with a comprehensive assessment.',
          conclusion: 'Thanks to this comprehensive approach, turbine owners and operators have complete information about the condition of their assets and can develop proactive maintenance strategies. As a result, turbine life is extended and unexpected failure times are minimized.'
        },
        droneRobotInspection: {
          title: 'Comprehensive Turbine Inspection with Robot and Drone',
          subtitle: 'Turbine damage detection from three different perspectives with robot, drone and operator',
          date: 'January 10, 2026',
          readTime: '10 min read',
          heroTitle: 'Multi-Layer Inspection Approach',
          introParagraph: 'VentraWind\'s turbine inspection methodology is based on the cooperation of robot, drone and expert operator teams. This triple approach ensures comprehensive inspection of the turbine from every angle and depth.',
          imageCaption1: 'Robot Inspection',
          imageCaption2: 'Drone Scanning',
          imageCaption3: 'Operator Control',
          gridCaption: 'Blade interior inspection with robot, exterior surface with drone, critical component inspection with operator',
          processParagraph: 'Our inspection process covers every component of the turbine with a systematic approach. First, robot systems are sent into the blades, then external surface scanning is performed with drones, and finally operators physically check critical points.',
          conclusion: 'Thanks to this comprehensive inspection methodology, potential problems in turbines are detected at an early stage and major failures are prevented. Our customers receive reports with complete transparency and reliable data about the condition of their turbines.'
        },
        windTurbineTechnology: {
          title: 'Wind Turbines: Working Principle and Usage in Türkiye',
          subtitle: 'Clean electricity production from the power of wind energy',
          date: 'January 5, 2026',
          readTime: '12 min read',
          heroTitle: 'Wind Energy: Clean and Sustainable Power Source',
          introParagraph: 'Wind turbines are renewable energy systems that convert wind\'s kinetic energy into electrical energy. Modern wind turbines are equipped with blades with diameters of 40-80 meters on towers 50-150 meters high, and each can generate electricity in the range of 2-5 MW.',
          workingTitle: 'How Do Wind Turbines Work?',
          workingParagraph1: 'The working principle of wind turbines is simple but effective. When wind hits the turbine blades, the blades start to rotate. This rotational motion is transferred to the generator located in the nacelle on top of the turbine. The generator converts this mechanical energy into electrical energy.',
          workingParagraph2: 'Modern turbines can automatically orient according to wind direction and dynamically adjust blade angles. This allows them to operate with optimal efficiency at wind speeds between 3-25 m/s. Turbines automatically stop for safety when wind speeds are excessively high.',
          turkeyTitle: 'Wind Energy in Türkiye',
          turkeyParagraph: 'Türkiye has high wind energy potential thanks to its geographical location and climatic characteristics. The installed wind energy capacity in our country has exceeded 11,000 MW and this capacity is increasing every year.',
          regionsTitle: 'Regions in Türkiye with Highest Wind Energy Production',
          region1Name: 'İzmir - Çeşme, Karaburun, Bergama',
          region1Desc: 'Highest potential in the Aegean Region',
          region2Name: 'Balıkesir - Bandırma, Gönen',
          region2Desc: 'Important wind corridor of Marmara',
          region3Name: 'Çanakkale - Bozcaada, Gelibolu',
          region3Desc: 'Meeting point of sea and land winds',
          region4Name: 'Hatay - İskenderun',
          region4Desc: 'Strong wind corridor of Mediterranean',
          region5Name: 'Osmaniye - Bahçe',
          region5Desc: 'High potential of South Anatolia',
          region6Name: 'Yalova - Çınarcık',
          region6Desc: 'Marmara Sea coastal region',
          region7Name: 'Manisa - Akhisar, Soma',
          region7Desc: 'Wind potential of inner Aegean',
          region8Name: 'Afyonkarahisar - Sandıklı',
          region8Desc: 'High altitude regions of inner Anatolia',
          advantagesTitle: 'Advantages of Wind Energy',
          advantagesParagraph: 'Wind energy is a completely clean and renewable energy source. It does not emit harmful gases into the atmosphere, does not require water consumption, and has no fuel cost. A wind turbine can generate uninterrupted electricity for 20-25 years after installation.',
          conclusion: 'As VentraWind, we provide advanced inspection and maintenance technologies to ensure the efficient and safe operation of these clean energy sources. We ensure that your turbines operate at maximum performance and have a long service life.'
        },
        worldsBiggestTurbine: {
          title: 'World\'s Largest Wind Turbine',
          subtitle: 'A new era with 16 MW power capacity',
          date: 'January 20, 2026',
          readTime: '7 min read',
          heroTitle: 'Giant Turbines: The Future of Wind Energy',
          introParagraph: 'Wind energy technology has made incredible progress in recent years. The world\'s largest wind turbine has reached a power capacity of 16 MW, reaching a level where it can meet the electricity needs of 20,000 households alone. These giant structures have a height of 260 meters and blades 123 meters long.',
          specTitle: 'Technical Specifications',
          specParagraph: 'Modern mega turbines are gigantic structures with rotor diameters exceeding 240 meters. Blade tip speeds can reach 350 km/h and when operating at full capacity, they can generate 80,000 kWh of electricity per hour. Each of these turbines can produce an average of 80 GWh of energy per year.',
          powerTitle: 'Energy Production Capacity',
          powerParagraph: 'A 16 MW turbine can meet the electricity needs of approximately 20,000 homes per year. This capacity means 8 times more energy production compared to traditional 2 MW turbines. Additionally, the environmental impact is minimized by installing fewer turbines.',
          futureTitle: 'Technology of the Future',
          futureParagraph: 'Manufacturers aim to develop turbines with capacities of 20 MW and above in the coming years. Efficiency is constantly increasing thanks to AI-powered control systems, advanced material technologies and optimized blade designs.',
          conclusion: 'As VentraWind, we use the most advanced technologies in the inspection and maintenance of these giant turbines. With our robotic systems, drone technology and expert teams, we ensure the safe and efficient operation of these massive structures.'
        },
        ropeAccessTurbine: {
          title: 'Rope Access Techniques in Wind Turbines',
          subtitle: 'Safe working at height and effective maintenance',
          date: 'January 18, 2026',
          readTime: '9 min read',
          heroTitle: 'Rope Access: Critical Method in Turbine Maintenance',
          introParagraph: 'Rope access techniques are one of the most critical methods in the maintenance and repair of wind turbines. Our expert technicians, who can work at heights exceeding 100 meters, are equipped with special training and certifications and can work safely on the outer surfaces of turbine blades.',
          safetyTitle: 'Safety and Training',
          safetyParagraph: 'Rope access technicians receive training according to IRATA (Industrial Rope Access Trade Association) or equivalent international standards. Each technician has comprehensive training in dual safety systems, fall prevention equipment and emergency procedures. All work is carried out with careful evaluation of wind speed and weather conditions.',
          advantagesTitle: 'Advantages of Rope Access',
          advantage1: 'Rapid Response: Quick access to the turbine without the need for platform or crane setup.',
          advantage2: 'Cost Effectiveness: Maintenance costs are reduced as it does not require heavy equipment and vehicles.',
          advantage3: 'Flexibility: Offers the ability to work in all weather conditions and on all turbine models.',
          advantage4: 'Detailed Inspection: Technicians can closely inspect every point on the turbine surface and detect even small damages.',
          conclusion: 'As VentraWind, with our experienced rope access team, we safely perform external surface maintenance, damage repair and routine inspections of your turbines. Our expert technicians are trained to high standards and have international certifications.'
        },
        offshoreTurbines: {
          title: 'Offshore Wind Turbines: Energy Potential of the Seas',
          subtitle: 'High capacity energy production in open seas',
          date: 'January 22, 2026',
          readTime: '10 min read',
          heroTitle: 'Offshore Wind Energy: Unlimited Potential',
          introParagraph: 'Offshore wind turbines have much higher energy production potential than land-based turbines. Wind speeds are higher and more continuous over the sea. Modern offshore turbines can be installed on fixed-bottom or floating platforms.',
          advantagesTitle: 'Advantages of Offshore Turbines',
          advantagesParagraph: 'Offshore turbines can generate 40-50% more energy than onshore turbines. Turbines work more efficiently because there are no wind barriers at sea. Additionally, being far from residential areas provides great advantages in terms of visual and noise pollution. Offshore wind farms can reach thousands of megawatts of capacity.',
          challengesTitle: 'Challenges and Solutions',
          challengesParagraph: 'Installation and maintenance of offshore turbines is more costly and technically challenging than onshore turbines. Factors such as salt water corrosion, wave and current effects, and harsh weather conditions must be taken into account. However, with developing technology, these challenges are being overcome and offshore wind energy is becoming increasingly economical.',
          turkeyTitle: 'Offshore Wind Potential in Türkiye',
          turkeyParagraph: 'Türkiye has high offshore wind potential thanks to being surrounded by seas on three sides. Offshore wind projects are being planned especially along the Aegean Sea, Marmara and Black Sea coasts. With the first offshore wind farms, Türkiye will be able to significantly increase its renewable energy capacity.',
          conclusion: 'As VentraWind, with our team specialized in the inspection and maintenance of offshore wind turbines, we aim to provide service at the highest standards even in these challenging environments. We support your offshore projects with our equipment suitable for marine conditions and experienced staff.'
        }
      }
    },

    ui: {
      videoLabel: 'Video',
      robotInspection: 'Robot Inspection',
      goToMedia: 'Go to media',
      turbine: 'Turbine'
    },

    robot: {
      hero: {
        advancedTech: 'Advanced Technology',
        liveInspection: 'Live Inspection',
        expertTeam: 'Expert Team',
        precisionScanning: 'Precision Scanning',
        controlCenter: 'Control Center',
        damageAnalysis: 'Damage Analysis',
        techTitle: 'Robotic Inspection Technology',
        inspectionTitle: 'Inside Turbine Robot Inspection',
        teamTitle: 'Expert Operator Team',
        processTitle: 'Detailed Inspection Process',
        centerTitle: 'Modern Control Center',
        bladeTitle: 'Blade Interior Inspection',
        techDesc: 'We provide detailed turbine interior inspection and maintenance services with our advanced robotic systems.',
        inspectionDesc: 'Our robots perform precise inspection and data collection inside the turbine.',
        teamDesc: 'Our experienced operator team remotely controls robot systems and performs analysis.',
        processDesc: 'Our robot systems detect damage by scanning turbine components with millimeter precision.',
        centerDesc: 'All robot operations are monitored and managed in our modern control center.',
        bladeDesc: 'High-resolution imaging and damage analysis on the interior surfaces of turbine blades.'
      },
      damage: {
        badge: 'Detection Examples',
        title: 'Damage Detection',
        description: 'Examples of precise damage detection performed with our robotic technology',
        detected: 'Damage Detected',
        detection1: 'Damage Detection - 1',
        detection2: 'Damage Detection - 2',
        detection3: 'Damage Detection - 3',
        detection4: 'Damage Detection - 4'
      },
      gallery: {
        badge: 'Photo Archive',
        title: 'Gallery',
        description: 'Images from our turbine inspection and maintenance processes',
        robotOp: 'Robot Operation',
        equipment: 'Technical Equipment',
        interior: 'Turbine Interior View',
        upper: 'Turbine Upper Section',
        platform: 'Upper Platform',
        general: 'Turbine General View',
        control: 'Control Room',
        bladeInspection: 'Robot Blade Interior Inspection',
        detailedInspection: 'Detailed Blade Interior Inspection',
        bladeScanning: 'Blade Interior Scanning',
        blade: 'Turbine Blade',
        surface: 'Blade Exterior Surface'
      }
    },
    
    errors: {
      notFound: 'Page not found',
      serverError: 'Server error',
      unauthorized: 'You do not have access to this page',
      networkError: 'Network connection error',
      tryAgain: 'Try Again',
      goHome: 'Go Home',
      title: 'Error',
      generic: 'An unexpected error occurred. Please try again.',
      network: 'Cannot reach the server. Check your connection and try again.',
      timeout: 'The request timed out. Please try again.',
      sessionExpired: 'Your session has expired. Please sign in again.',
      permission: 'You are not authorized to perform this action.',
      validation: 'There is a problem with the information you entered. Please check and try again.',
      conflict: 'This record already exists.',
      rateLimit: 'Too many requests. Please wait a moment and try again.',
      server: 'A server error occurred. Please try again later.',
      notFoundResource: 'The requested record was not found.',
      loadFailed: 'Could not load data.',
      retry: 'Try again',
      dismiss: 'Dismiss',
      fileTooLarge: 'File is too large. Please choose a smaller file.',
      invalidFileType: 'This file type is not accepted.',
      partialUploadTitle: 'Some files could not be uploaded',
      partialUploadSuffix: 'file(s) could not be uploaded',
      renderCrashTitle: 'Something went wrong',
      renderCrashBody: 'This section failed to render. Try reloading the page.',
      reloadPage: 'Reload page'
    }
  }
}

export default strings